import React from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { Box, Chip } from '@mui/material';
import StatusBadge from '../common/StatusBadge';
import { LEAD_STATUSES } from '../../config/constants';

const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return '-';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(amount);
};

const formatDate = (date) => {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const columns = [
  { field: 'fileNumber', headerName: 'File #', width: 110, sortable: true },
  { field: 'name', headerName: 'Name', flex: 1, minWidth: 150, sortable: true },
  { field: 'phone', headerName: 'Phone', width: 130, sortable: false },
  {
    field: 'branch',
    headerName: 'Branch',
    width: 110,
    valueGetter: (value) => value || '-',
  },
  {
    field: 'leadStatus',
    headerName: 'Lead',
    width: 130,
    renderCell: (params) => {
      const ls = LEAD_STATUSES.find((s) => s.value === params.value);
      if (!ls) return '-';
      return (
        <Chip
          label={ls.label}
          size="small"
          sx={{ bgcolor: ls.color, color: '#fff', fontWeight: 500, fontSize: '0.7rem' }}
        />
      );
    },
  },
  { field: 'course', headerName: 'Course', flex: 1, minWidth: 130 },
  { field: 'country', headerName: 'Country', width: 120 },
  { field: 'interestedBank', headerName: 'Bank', width: 130 },
  {
    field: 'loanAmount',
    headerName: 'Loan Amount',
    width: 140,
    type: 'number',
    valueFormatter: (value) => formatCurrency(value),
  },
  {
    field: 'consultancyId',
    headerName: 'Consultancy',
    width: 140,
    valueGetter: (value) => value?.name || '-',
  },
  {
    field: 'status',
    headerName: 'Status',
    width: 170,
    renderCell: (params) => <StatusBadge status={params.value} />,
    sortable: true,
  },
  {
    field: 'managerId',
    headerName: 'Manager',
    width: 130,
    valueGetter: (value) => value?.name || '-',
  },
  {
    field: 'createdAt',
    headerName: 'Created',
    width: 120,
    valueFormatter: (value) => formatDate(value),
    sortable: true,
  },
];

const StudentTable = ({
  students = [],
  total = 0,
  page = 0,
  pageSize = 25,
  onPageChange,
  onRowClick,
  loading = false,
}) => {
  const handlePaginationModelChange = (model) => {
    if (onPageChange) onPageChange(model.page, model.pageSize);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <DataGrid
        rows={students}
        columns={columns}
        getRowId={(row) => row._id || row.id}
        rowCount={total}
        loading={loading}
        pageSizeOptions={[10, 25, 50]}
        paginationMode="server"
        paginationModel={{ page, pageSize }}
        onPaginationModelChange={handlePaginationModelChange}
        onRowClick={(params) => onRowClick && onRowClick(params.row)}
        disableRowSelectionOnClick
        autoHeight
        sx={{
          border: 'none',
          borderRadius: 2,
          fontFamily: '"Google Sans", Roboto, sans-serif',
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#F8F9FA',
            borderBottom: '1px solid #DADCE0',
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 500,
              fontSize: '0.75rem',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: '#5F6368',
            },
          },
          '& .MuiDataGrid-cell': {
            borderBottom: '1px solid #E8EAED',
            fontSize: '0.8125rem',
            color: '#202124',
            py: 1.25,
          },
          '& .MuiDataGrid-row': {
            cursor: onRowClick ? 'pointer' : 'default',
            '&:hover': {
              backgroundColor: '#F8F9FA',
            },
          },
          '& .MuiDataGrid-footerContainer': {
            borderTop: '1px solid #DADCE0',
          },
          '& .MuiDataGrid-overlay': {
            backgroundColor: 'rgba(241,243,244,0.5)',
          },
        }}
      />
    </Box>
  );
};

export default StudentTable;
